import { styled } from '@stitches/react';
import Stack from 'components/commons/stack';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import { HeaderText, ItemCard, SmallLabel, styles, ValueText } from './style';
import { AbsolutePosition, TabItemContainer } from '../style';

interface Props {
  data: {
    type: 'passenger' | 'vehicle' | 'vehicle-only';
    passengerName?: string;
    identityId?: string;
    seatNumber?: string;
    roomNumber?: string;
    platNumber?: string;
    driverName?: string;
    headerText: string;
    passenger?: string;
    expedition?: string;
    condition?: string;
    content?: string;
    vehicleDetail?: string;
    document?: string[];
  }[];
}

export default function PassengerDetail(props: Props) {
  const { t } = useTranslation();

  const linkStyles: React.CSSProperties = {
    textDecoration: 'none',
  };
  return (
    <TabItemContainer>
      <AbsolutePosition>
        {props.data.map((item, idx) => (
          <ItemCard key={`${item.type}-${idx}`}>
            <Stack
              direction="horizontal"
              justify="between"
              rootCss={{ borderBottom: '1px solid $dbGray1', mb: 12, pb: 12 }}
            >
              <HeaderText>{item.headerText}</HeaderText>
            </Stack>
            {item.type === 'passenger' ? (
              <Stack
                direction="vertical"
                gap="md"
                wrap
                justify="between"
                rootCss={{ '@md': { flexDirection: 'row' } }}
              >
                <Stack.Item css={styles.content}>
                  <Stack.Item css={{ flex: 1 }}>
                    <SmallLabel>{t('common:passenger_name')}</SmallLabel>
                    <ValueText>{item.passengerName}</ValueText>
                  </Stack.Item>
                  {item.identityId && (
                    <Stack.Item css={{ flex: 1 }}>
                      <SmallLabel>{t('common:identity_number')}</SmallLabel>
                      <ValueText>{item.identityId || '-'}</ValueText>
                    </Stack.Item>
                  )}
                </Stack.Item>
                <Stack.Item css={styles.content}>
                  {item.seatNumber ? (
                    <Stack.Item css={{ flex: 1 }}>
                      <SmallLabel>{t('common:seat_number')}</SmallLabel>
                      <ValueText>{item.seatNumber || '-'}</ValueText>
                    </Stack.Item>
                  ) : item.roomNumber !== '-' ? (
                    <Stack.Item css={{ flex: 1 }}>
                      <SmallLabel>
                        {t('booking:facility_number_short')}
                      </SmallLabel>
                      <ValueText>{item.roomNumber || '-'}</ValueText>
                    </Stack.Item>
                  ) : (
                    <></>
                  )}
                </Stack.Item>
              </Stack>
            ) : (
              <>
                <Stack direction="horizontal" gap="md" wrap justify="between">
                  <Stack.Item direction="horizontal" grow wrap>
                    <Stack.Item
                      css={{ flex: 1, minWidth: '30%', marginBottom: '16px' }}
                    >
                      <SmallLabel>
                        {t('common:vehicle_registration_number')}
                      </SmallLabel>
                      <ValueText>{item.platNumber?.toUpperCase()}</ValueText>
                    </Stack.Item>
                    {item.type === 'vehicle' ? (
                      <>
                        <Stack.Item
                          css={{
                            flex: 1,
                            minWidth: '30%',
                            marginBottom: '16px',
                          }}
                        >
                          <SmallLabel>{t('common:driver_name')}</SmallLabel>
                          <ValueText>{item.driverName}</ValueText>
                        </Stack.Item>
                        <Stack.Item
                          css={{
                            overflowX: 'scroll',
                            width: '100%',
                            '@md': {
                              flex: 1,
                              width: 'initial',
                              minWidth: '30%',
                              marginBottom: '16px',
                            },
                          }}
                        >
                          <SmallLabel>{t('common:passenger')}</SmallLabel>
                          <ValueText>{item.passenger}</ValueText>
                        </Stack.Item>
                      </>
                    ) : (
                      <>
                        <Stack.Item
                          css={{
                            flex: 1,
                            minWidth: '30%',
                            marginBottom: '16px',
                          }}
                        >
                          <SmallLabel>{t('booking:content')}</SmallLabel>
                          <ValueText>{item.content || '-'}</ValueText>
                        </Stack.Item>
                        <Stack.Item
                          css={{
                            flex: 1,
                            minWidth: '30%',
                            marginBottom: '16px',
                          }}
                        >
                          <SmallLabel>{t('booking:expedition')}</SmallLabel>
                          <ValueText>{item.expedition}</ValueText>
                        </Stack.Item>
                        <Stack.Item
                          css={{
                            flex: 1,
                            minWidth: '30%',
                            marginBottom: '16px',
                          }}
                        >
                          <SmallLabel>{t('booking:condition')}</SmallLabel>
                          <ValueText>{t(`common:${item.condition}`)}</ValueText>
                        </Stack.Item>
                        <Stack.Item
                          css={{
                            flex: 1,
                            minWidth: '30%',
                            marginBottom: '16px',
                          }}
                        >
                          <SmallLabel>{t('booking:vehicle_detail')}</SmallLabel>
                          <ValueText>{item.vehicleDetail}</ValueText>
                        </Stack.Item>
                      </>
                    )}
                  </Stack.Item>
                </Stack>
                {item?.document && (
                  <Stack.Item
                    css={{
                      flex: 1,
                      minWidth: '30%',
                      marginBottom: '16px',
                    }}
                  >
                    <SmallLabel>{t('booking:condition')}</SmallLabel>
                    <ImageContainer>
                      {item?.document.map((cur) => {
                        return (
                          <Link
                            key={cur}
                            target="_blank"
                            href={cur}
                            style={linkStyles}
                          >
                            <Image alt="detail" src={cur} />
                          </Link>
                        );
                      })}
                    </ImageContainer>
                  </Stack.Item>
                )}
              </>
            )}
          </ItemCard>
        ))}
      </AbsolutePosition>
    </TabItemContainer>
  );
}

const ImageContainer = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
});

const Image = styled('img', {
  width: 200,
  height: 120,
  objectFit: 'cover',
  marginRight: 16,
  backgroundColor: 'white',
  boxShadow:
    'rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px',
  '&:hover': {
    transform: 'scale(1.1)',
  },
});
